import React, { useState } from 'react';
import { ampli } from '@/ampli';
import { useAppDispatch, useAppSelector } from '@/features';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { Badge, Button, IconButton } from '@mui/material';
import Popover from '@mui/material/Popover';
import { Link } from '@tanstack/react-router';
import CircularProgress from 'components/CircularProgress';
import { closeNotifications, openNotifications } from 'features/Common';
import { quickNotificationsOpen } from 'features/selectors';
import { useWindowDimensions } from 'hooks/useWindowDimensions';
import { isMobile } from 'react-device-detect';
import IntlMessages from 'util/IntlMessages';

import NotificationsContent from '../NotificationsContent';

const NotificationsQuickView: React.FC = () => {
  const { width } = useWindowDimensions();
  const dispatch = useAppDispatch();
  const notifications = useAppSelector((state) => state.notifications.notifications);
  const notificationsOpen = useAppSelector(quickNotificationsOpen);
  const unseenNotificationsCount = useAppSelector((state) => state.notifications.unseenCount);

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    ampli.notificationsBellSelected();
    setAnchorEl(event.currentTarget);
    dispatch(openNotifications());
  };

  const handleCloseNotifications = () => {
    dispatch(closeNotifications());
    setAnchorEl(null);
  };

  return notifications ? (
    <>
      <IconButton
        onClick={handleOpen}
        size="small"
        className="mx-2"
        autoFocus={false}
        sx={{ position: 'relative' }}
      >
        <Badge badgeContent={unseenNotificationsCount} color="error">
          <NotificationsIcon />
        </Badge>
      </IconButton>
      <Popover
        anchorEl={anchorEl}
        open={notificationsOpen}
        onClose={handleCloseNotifications}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: width <= 425 ? '100%' : 500,
            height: '80%',
            maxHeight: '748px',
            borderRadius: 2,
            scrollbarWidth: 0,
            position: 'relative',
            top: `${isMobile || width <= 560 ? 88 : 37}px !important`,
            overflow: 'hidden',
            margin: 0,
            transition: 'all 0.1s ease-in !important',
            '&::-webkit-scrollbar': { display: 'none' },
          },
        }}
      >
        <NotificationsContent size="small" closeNotifications={handleCloseNotifications} />
        <Link to="/notifications" onClick={() => handleCloseNotifications()}>
          <Button
            className="see-all-notifications"
            sx={{ borderRadius: 0, height: '10% !important' }}
          >
            <IntlMessages id="pages.notifications-quick-view.see-all" />
          </Button>
        </Link>
      </Popover>
    </>
  ) : (
    <CircularProgress />
  );
};

export default NotificationsQuickView;
